// What the console reads and sends about a customer's CrowdStrike Falcon (WRMM-229). Shared with apps/console.

/**
 * A child to link that Falcon's Provisioning API doesn't manage (made in the Partner Portal): a public 409 code, and the
 * fixed words the owner is told, with what to do instead (WRMM-513).
 */
export const FALCON_PORTAL_CHILD_CODE = 'falcon_portal_child';
export const FALCON_PORTAL_CHILD = 'That child was made in the Partner Portal, not through Falcon’s Provisioning API, so wittco-rmm can’t manage it or install '
  + 'Falcon into it. Install the sensor on its computers from the Falcon console instead';

/** `none`: no child and none asked for. `requested`: Falcon is creating it. `completed`: the customer's child. `failed`: given up. */
export type FalconChildState = 'none' | 'requested' | 'completed' | 'failed';
/** Why the worker gave up, in fixed words: never Falcon's own text. */
export type FalconChildFailure = 'failed' | 'refused' | 'name_taken' | 'similar_name' | 'already_linked' | 'withdrawn';
/** Why the last check didn't finish it while the worker keeps trying. */
export type FalconChildSetback = 'failed' | 'refused' | 'unverified' | 'unknown' | 'error' | 'slow';

/** Whether this server can create children for this workspace, and what every child gets. */
export interface FalconProvisioningView {
  connected: boolean;
  defaults: { bundles: string[]; adminEmail: string; domains: string[] } | null;
}

export interface FalconChildView extends FalconProvisioningView {
  /** The customer's CrowdStrike box: null for a customer from before it existed, not decided yet. */
  protection: boolean | null;
  state: FalconChildState;
  /** `manual`: a CID entered by hand in the customer's migration links, with no request or link here. */
  source: 'provisioned' | 'linked' | 'manual' | null;
  cid: string | null;
  /** The child's name, as last asked for. */
  request: { name: string } | null;
  requestedAt: string | null; sentAt: string | null; nextCheckAt: string | null;
  /** Attempts so far, of at most `maxAttempts`, and why the last check didn't finish it, while it keeps trying. */
  attempts: number; maxAttempts: number; setback: FalconChildSetback | null;
  completedAt: string | null; failedAt: string | null; failure: FalconChildFailure | null;
  /** The child under the parent whose name is or resembles the customer's (`name_taken`, `similar_name`): link it, or create anyway. */
  match: { cid: string; name: string } | null;
  /** Falcon may have the request, but its answer to the send hasn't come: settled at the next status check. */
  sendUncertain: boolean;
  /** An alert about this request waiting to go to the workspace's alert topic, and when the last one went (null: none). */
  alertQueued: boolean; alertSentAt: string | null;
}

/** Sent with a new customer: its CrowdStrike box, ticked or not. */
export type NewCustomerFalcon = boolean;

/** One of the customer's computers, and whether it waits for Falcon (WRMM-229, part 2): `reason` says why not. */
export interface FalconInstallComputer {
  deviceId: string; hostname: string; displayName?: string | null; supportId?: string | null; siteId: string; siteName: string | null; lastSeenAt: string | null;
  waiting: boolean; reason: string | null; sentinelOne: boolean;
  /** Current-site group check after the computer moved. Unknown never means a match. */
  grouping?: { status: 'frozen' | 'mismatch' | 'unknown' | 'matched'; checkedAt?: string; expectedGroupId: string | null; expectedTag: string | null; message: string };
  /** Set to have no CrowdStrike ("remote access only"), with its reason: it never waits. */
  bypass: FalconBypass | null;
  /** Whether it can be set to have no CrowdStrike now: not set already, and Falcon isn't on it or on its way. */
  bypassable: boolean;
  /**
   * Its platform, and which Falcon sensor it takes (WRMM-316): one install batch is one site's computers of one key
   * ("windows", "macos", or "linux/<distribution>/<major>/<architecture>"). Null: Falcon has no sensor for it.
   */
  platform: 'windows' | 'linux' | 'macos' | null; sensorKey: string | null;
}
/** A computer set to have no CrowdStrike (WRMM-229, part 3): an owner's reason, confirmed with their key. */
export interface FalconBypass { reason: string; setAt: string }
/** One computer's Falcon install job, from its request until Falcon protects the computer (`done`) or it ends otherwise. */
export interface FalconInstall {
  s1RemovedAt: string | null;
  id: string; deviceId: string; hostname: string; displayName?: string | null; supportId?: string | null; siteId: string; batchId: string; jobId: string;
  /** `canceled`: its job never ran (rejected, canceled or expired), so nothing was installed and no alert went. */
  state: 'requested' | 'done' | 'failed' | 'canceled'; detail: string | null; falconAid: string | null;
  requestedAt: string; protectedAt: string | null; endedAt: string | null; alertedAt: string | null;
  platform: 'windows' | 'linux' | 'macos';
}
/** The customer's Falcon installs: why none can be requested (`unavailable`), its sites, computers and recent installs. */
export interface FalconInstalls {
  /** Whether the server can install Falcon (FALCON_INSTALL_ENABLED with the runtime client and the workspace's alert topic). */
  connected: boolean;
  unavailable: string | null;
  sites: Array<{ siteId: string; siteName: string; linked: boolean }>;
  computers: FalconInstallComputer[];
  /** The customer's computers set to have no CrowdStrike, shown (and clearable) even when installs can't be asked for. */
  bypassed: Array<Pick<FalconInstallComputer, 'deviceId' | 'hostname'> & { bypass: FalconBypass }>;
  installs: FalconInstall[];
  /** How many of the customer's installs are still open, counted in full (`installs` holds only the last 50). */
  installing: number;
  /** Whether package compatibility checks are on: then each request carries the chosen sensor's reviewed requirements. */
  compatibility: boolean;
  /**
   * The sensors Falcon offers the customer's child for its waiting computers, newest first, each with the sensor keys
   * it fits; null when Falcon wasn't asked or didn't answer. WRMM-399: none newer than the build the waiting sites'
   * sensor update policy pins, and that build first (`pinned`), when Falcon's policies can be read.
   */
  sensors: Array<{ sha256: string; version: string; releasedAt: string | null; sensorKeys: string[]; pinned?: boolean }> | null;
}

/** WRMM-363: removal only, for customers with CrowdStrike off or computers with an explicit bypass. */
export interface SentinelOneRemovalComputer {
  deviceId: string; hostname: string; displayName?: string | null; supportId?: string | null; siteId: string; siteName: string | null;
  cause: 'customer_off' | 'bypass' | null; waiting: boolean; reason: string | null;
}
export interface SentinelOneRemoval {
  id: string; deviceId: string; hostname: string; displayName?: string | null; supportId?: string | null; siteId: string; batchId: string; jobId: string;
  cause: 'customer_off' | 'bypass'; state: 'requested' | 'done' | 'failed' | 'canceled';
  defender: 'on' | 'off' | 'unknown' | null; detail: string | null;
  requestedAt: string; removedAt: string | null; endedAt: string | null;
}
export interface SentinelOneRemovals {
  connected: boolean; computers: SentinelOneRemovalComputer[]; removals: SentinelOneRemoval[]; removing: number;
}
